/**
 * Adapters between the backend API shape (per OpenAPI /search) and the UI.
 *
 * The API's `Profile` already includes all nested arrays on every search
 * result — there is no separate detail endpoint.
 * profileDetailFromApi maps the full shape consumed by ProfileView.
 * paginatedFromApi uses it so every search result carries detail data,
 * enabling the profile view without a second network request.
 */

// -- helpers ----------------------------------------------------------------

const TIER_ORDER = ['Millionaire', 'Decamillionaire', 'Centi-millionaire', 'Billionaire'];

// Treat API string-nulls ("null", "undefined", "N/A", "n/a", "none") as empty.
const NULL_STRINGS = new Set(['null', 'undefined', 'n/a', 'none', 'na']);
function clean(v) {
  if (v == null) return '';
  const s = String(v).trim();
  return NULL_STRINGS.has(s.toLowerCase()) ? '' : s;
}

// A handful of ApiProfile fields (alias_names, citizenship_country,
// languages_spoken, primary_industry) are arrays of strings per the OpenAPI
// schema, not semicolon-joined strings — join them for display.
function cleanList(arr, sep = '; ') {
  if (!Array.isArray(arr)) return clean(arr);
  return arr.map(clean).filter(Boolean).join(sep);
}

function initialsFrom(fullName) {
  if (!fullName) return '··';
  const parts = fullName
    .replace(/[^\p{L}\s.-]/gu, '')
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '··';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function pickPrimaryCompany(companies) {
  if (!Array.isArray(companies) || companies.length === 0) return null;
  const primary = companies.find((c) => String(c.primary_employment).toLowerCase() === 'true');
  if (primary) return primary;
  const sorted = [...companies].sort((a, b) =>
    String(b.date_position_held_from || '').localeCompare(String(a.date_position_held_from || '')),
  );
  return sorted[0];
}

function tierFromBanding(banding) {
  if (!banding) return '';
  const b = String(banding).toLowerCase();
  if (b.includes('billion')) return 'Billionaire';
  if (b.includes('centi') || b.includes('100m')) return 'Centi-millionaire';
  if (b.includes('deca') || b.includes('10m')) return 'Decamillionaire';
  if (b.includes('million') || b.includes('1m')) return 'Millionaire';
  if (TIER_ORDER.includes(banding)) return banding;
  return banding;
}

// Format a raw USD numeric string (or already-formatted string) for display.
// Returns '' for empty/zero/unparseable input so `field && ...` guards keep working.
function formatUsd(raw) {
  if (!raw) return '';
  const n = Number(raw);
  if (Number.isFinite(n) && n !== 0) {
    const sign = n < 0 ? '-' : '';
    const abs = Math.abs(n);
    if (abs >= 1_000_000_000) return `${sign}USD ${(abs / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
    if (abs >= 1_000_000) return `${sign}USD ${(abs / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    if (abs >= 1_000) return `${sign}USD ${(abs / 1_000).toFixed(0)}K`;
    return `${sign}USD ${abs.toLocaleString()}`;
  }
  // Not a plain number — likely already human-formatted (e.g. "USD 4.2B"); pass through.
  return typeof raw === 'string' ? raw.trim() : '';
}

function formatNetWorth(estimatedUsd, banding) {
  return formatUsd(estimatedUsd) || clean(banding) || '-';
}

function formatUpdated(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateOfBirth(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function pickAlmaMater(education) {
  if (!Array.isArray(education) || education.length === 0) return '';
  const sorted = [...education].sort((a, b) =>
    String(b.year_of_completion || '').localeCompare(String(a.year_of_completion || '')),
  );
  return sorted[0]?.organisation_name || '';
}

function deriveRiskTone(profile) {
  // If litigation or bankruptcy is non-empty, treat as review or hit.
  const hasLitigation = Array.isArray(profile.litigation) && profile.litigation.length > 0;
  const hasBankruptcy = Array.isArray(profile.bankruptcy) && profile.bankruptcy.length > 0;
  const status = String(profile.status || '').toLowerCase();
  if (status.includes('hit') || status.includes('sanction') || hasBankruptcy) return 'hit';
  if (status.includes('review') || status.includes('flag') || hasLitigation) return 'review';
  return 'cleared';
}

function deriveRiskScore(tone) {
  return tone === 'hit' ? 7.8 : tone === 'review' ? 4.2 : 1.6;
}

function computeAge(dob, yearOfBirth) {
  if (dob) {
    const birth = new Date(dob);
    if (!Number.isNaN(birth.getTime())) {
      const now = new Date();
      let age = now.getFullYear() - birth.getFullYear();
      if (
        now.getMonth() < birth.getMonth() ||
        (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())
      ) age--;
      return age;
    }
  }
  if (yearOfBirth) {
    const y = Number(yearOfBirth);
    if (Number.isFinite(y) && y > 1900) return new Date().getFullYear() - y;
  }
  return '-';
}

// Derive initials for a person name
function personInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// -- adapters ----------------------------------------------------------------

/**
 * Maps a Profile API object to the full UI shape consumed by both the search
 * cards/rows and ProfileView. Called for every result in the search response.
 *
 * @param {object} api - Profile as returned by GET /search
 * @returns {object}
 */
export function profileFromApi(api) {
  const company = pickPrimaryCompany(api.companies);
  const interests = Array.isArray(api.interests)
    ? api.interests.map((i) => i.interest_type_detail || i.interest_type).filter(Boolean)
    : [];

  const tier = tierFromBanding(api.net_worth_banding_usd);
  const tone = deriveRiskTone(api);
  const netWorth = formatNetWorth(api.net_worth_estimated_usd, api.net_worth_banding_usd);

  // Career — sort current positions first, then by start date descending.
  const career = (api.companies || [])
    .map((c) => ({
      id: c.id || '',
      role: clean(c.job_title),
      company: clean(c.company),
      industry: clean(c.industry_sector) || clean(c.industry),
      from: clean(c.date_position_held_from) || '-',
      to: clean(c.year_ending) && c.year_ending !== '0' ? clean(c.year_ending) : 'Present',
      details: clean(c.details),
      employmentType: clean(c.employment_type_name),
    }))
    .sort((a, b) => {
      const aPresent = a.to === 'Present' ? 1 : 0;
      const bPresent = b.to === 'Present' ? 1 : 0;
      if (aPresent !== bPresent) return bPresent - aPresent;
      return String(b.from).localeCompare(String(a.from));
    });

  // Education — most recent first.
  const education = (api.education || [])
    .map((e) => ({
      type: clean(e.education_type_name),
      degree: clean(e.name_of_course),
      school: clean(e.organisation_name),
      year: clean(e.year_of_completion),
    }))
    .sort((a, b) => String(b.year).localeCompare(String(a.year)));

  // Languages — three separate API fields.
  const languages = [api.language_1, api.language_2, api.language_3].map(clean).filter(Boolean);

  // City / country split: home_town may be "City, Country" or just "City".
  const hometown = String(api.home_town || '');
  const hometownParts = hometown.split(',').map((s) => s.trim());
  const city = hometownParts[0] || hometown;
  const country = api.domicile || hometownParts[1] || '';

  // --- Detail view data ---

  // Family members
  const family = (api.family_members || []).map((f) => ({
    id: f.family_member_id || '',
    name: clean(f.member_name),
    relationship: clean(f.relationship),
    membershipType: clean(f.membership_type),
    initials: personInitials(f.member_name),
  }));

  // Network / acquaintances
  const network = (api.acquaintances || []).map((a) => ({
    name: clean(a.acquaintance_name),
    details: clean(a.details),
    initials: personInitials(a.acquaintance_name),
  }));

  // Full interests with details
  const fullInterests = (api.interests || []).map((i) => ({
    type: clean(i.interest_type),
    typeDetail: clean(i.interest_type_detail) || clean(i.interest_type),
    details: clean(i.details),
  }));

  // Listed company ownership
  const listedOwnership = (api.listed_company_ownership || []).map((o) => ({
    year: clean(o.reference_year),
    company: clean(o.company),
    industry: clean(o.industry),
    country: clean(o.country_incorporation),
    exchange: clean(o.primary_stock_exchange),
    ticker: clean(o.stock_market_ticker),
    ownership: clean(o.ownership),
    marketCap: clean(o.market_capitalisation),
    value: clean(o.value_of_shareholding_usd),
    valueDisplay: formatUsd(o.value_of_shareholding_usd),
    details: clean(o.details),
    notes: clean(o.notes),
    type: 'listed',
  }));

  // Unlisted company ownership
  const unlistedOwnership = (api.unlisted_company_ownership || []).map((o) => ({
    year: clean(o.reference_year),
    company: clean(o.company_name),
    industry: clean(o.industry),
    country: clean(o.country_incorporation),
    ownership: clean(o.ownership),
    value: clean(o.estimated_value_usd),
    valueDisplay: formatUsd(o.estimated_value_usd),
    valuationDate: clean(o.date_of_valuation),
    details: clean(o.details),
    type: 'unlisted',
  }));

  // Other asset ownership
  const otherAssets = (api.other_asset_ownership || []).map((o) => ({
    year: clean(o.reference_year),
    name: clean(o.asset_name),
    assetType: clean(o.asset_type),
    location: clean(o.asset_location),
    ownership: clean(o.ownership),
    value: clean(o.estimated_value_usd),
    valueDisplay: formatUsd(o.estimated_value_usd),
    type: 'other',
  }));

  // Former ownership
  const formerAssets = (api.former_ownership || []).map((o) => ({
    name: clean(o.asset_name),
    assetType: clean(o.asset_type),
    location: clean(o.asset_location),
    from: clean(o.stake_held_from),
    to: clean(o.stake_held_to),
    ownership: clean(o.ownership),
    type: 'former',
  }));

  // News
  const fullNews = (api.news || [])
    .map((n) => ({
      date: clean(n.date),
      source: clean(n.source),
      newsType: clean(n.news_type),
      headline: clean(n.headline),
      details: clean(n.details),
      link: clean(n.link),
      personalOverview: clean(n.personal_overview),
      companyOverview: clean(n.company_overview),
    }))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));

  // Litigation
  const litigationList = (api.litigation || []).map((l) => ({
    dateFiled: clean(l.date_filed),
    dateClosed: clean(l.date_closed),
    type: clean(l.litigation_type),
    court: clean(l.court),
    country: clean(l.country_name),
    defendant: clean(l.defendant),
    plaintiff: clean(l.plaintiff),
    details: clean(l.details),
  }));

  // Bankruptcy
  const bankruptcyList = (api.bankruptcy || []).map((b) => ({
    overview: clean(b.overview),
    dateFiled: clean(b.date_filed),
    dateDischarge: clean(b.date_discharged),
    caseNumber: clean(b.case_number),
    filingType: clean(b.filing_type),
    court: clean(b.court),
    name: clean(b.name),
    details: clean(b.details),
  }));

  // Contact details
  const contacts = (api.contact_details || []).map((c) => ({
    id: c.contact_id || '',
    overview: clean(c.overview),
    type: clean(c.contact_type_name),
    isPrimary: String(c.primary_contact).toLowerCase() === 'true',
    officeName: clean(c.office_name),
    address1: clean(c.address1),
    address2: clean(c.address2),
    country: clean(c.country),
    city: clean(c.city),
    postalCode: clean(c.postal_code),
    website: clean(c.website),
    email: clean(c.email),
    phone: clean(c.telephone),
    personalEmail: clean(c.personal_email),
    mobile: clean(c.mobile_number),
  })).sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));

  // Advisors
  const advisorsList = (api.advisors || []).map((a) => ({
    overview: clean(a.overview),
    type: clean(a.advisor_type_name),
    relationship: clean(a.relationship),
    company: clean(a.advisor_company),
    name: clean(a.advisor_name),
    country: clean(a.country),
    city: clean(a.city),
    website: clean(a.website),
    email: clean(a.email),
  }));

  // Affiliations (merge affiliations + philanthropic_activities)
  const affiliations = [
    ...(api.affiliations || []).map((a) => ({
      org: clean(a.organisation_name),
      type: clean(a.type_of_organisation) || clean(a.organisation_type_detail),
      position: clean(a.position),
      location: clean(a.organisation_location),
      year: clean(a.year),
      value: formatUsd(a.estimated_usd_value),
      overview: clean(a.overview),
      category: 'affiliation',
    })),
    ...(api.philanthropic_activities || []).map((p) => ({
      org: clean(p.organisation_name),
      type: clean(p.organisation_type_name) || clean(p.organisation_type_detail),
      position: clean(p.position),
      location: clean(p.organisation_location),
      year: clean(p.year),
      value: formatUsd(p.estimated_usd_value),
      overview: clean(p.overview),
      category: 'philanthropic',
    })),
  ];

  // Honours / awards
  const honours = Array.isArray(api.honours) ? api.honours.map(clean).filter(Boolean) : [];

  // Other sources of wealth
  const otherSources = (api.other_sources_of_wealth || []).map((s) => ({
    year: clean(s.reference_year),
    name: clean(s.name),
    type: clean(s.type),
    typeDetail: clean(s.type_detail),
    estimatedTotal: formatUsd(s.estimated_total_usd),
  }));

  // Liquidity events
  const liquidityEvents = (api.liquidity_events || []).map((e) => ({
    type: clean(e.type),
    date: clean(e.completion_date),
    company: clean(e.company_name),
    industry: clean(e.industry),
    country: clean(e.country_incorporation),
    acquirer: clean(e.name_of_acquirer),
    cashUsd: formatUsd(e.cash_transaction_usd),
    otherUsd: formatUsd(e.other_transaction_usd),
    resultantCompany: clean(e.resultant_shareholding_company),
    resultantOwnership: clean(e.resultant_shareholding_ownership),
  }));

  // Super yachts
  const superYachts = (api.super_yachts || []).map((y) => ({
    name: clean(y.current_yacht),
    loa: clean(y.loa),
    former: clean(y.former_yacht),
    captain: clean(y.current_yacht_captain),
  }));

  // Private jets
  const privateJets = (api.private_jets || []).map((j) => ({
    make: clean(j.make),
    model: clean(j.model),
    serial: clean(j.serial),
    year: clean(j.year),
    ownership: clean(j.ownership),
    value: formatUsd(j.jet_value),
    details: clean(j.details),
  }));

  // Other information
  const otherInfo = (api.other_information || []).map((o) => ({
    overview: clean(o.overview),
    date: clean(o.date),
    details: o.details || '',
  }));

  return {
    // --- search card fields (do not change) ---
    id: String(api.id),
    name: api.full_name || [api.first_name, api.middle_name, api.sur_name].filter(Boolean).join(' '),
    initials: initialsFrom(api.full_name || api.known_as),
    gender: clean(api.gender),
    nationality: clean(api.nationality),
    domicile: clean(api.domicile),
    residence: clean(api.home_town) || clean(api.domicile),
    industry:
      (Array.isArray(api.industry) && clean(api.industry[0])) ||
      (company && clean(company.industry)) ||
      '',
    tier,
    netWorth,
    almaMater: pickAlmaMater(api.education),
    interests,
    company: (company && company.company) || '',
    role: (company && company.job_title) || '',
    pep: false,
    sanctions: false,
    adverseMedia: fullNews.filter(
      (n) => n.newsType && n.newsType.toLowerCase().includes('adverse'),
    ).length,
    riskScore: deriveRiskScore(tone),
    riskTone: tone,
    keywords: [],
    updated: formatUpdated(api.modified_date || api.created_date),

    // --- detail view fields (ProfileView) ---
    secondaryId: clean(api.secondary_id),
    title: clean(api.title),
    knownAs: clean(api.known_as),
    city,
    country,
    age: computeAge(api.date_of_birth, api.year_of_birth),
    dateOfBirth: formatDateOfBirth(api.date_of_birth),
    yearOfBirth: clean(api.year_of_birth),
    netWorthRange: clean(api.wealth_range) || netWorth,
    wealthRange: clean(api.wealth_range),
    liquidityWealth: formatNetWorth(api.liquidity_wealth_usd, api.liquid_wealth_banding_usd),
    sourceOfWealth: clean(api.wealth_primary_source),
    bio: clean(api.summary) || clean(api.profile_overview),
    maritalStatus: clean(api.marital_status),
    spouseName: clean(api.spouse_name),
    numberOfChildren: clean(api.number_of_children),
    religion: clean(api.religion),
    ethnicity: clean(api.ethnicity),
    otherNationality: clean(api.other_nationality),
    twitterLink: clean(api.twitter_link),
    linkedinLink: clean(api.linkedin_link),
    facebookLink: clean(api.facebook_link),
    // arrays
    education,
    career,
    languages,
    family,
    network,
    fullInterests,
    listedOwnership,
    unlistedOwnership,
    otherAssets,
    formerAssets,
    fullNews,
    litigationList,
    bankruptcyList,
    contacts,
    advisorsList,
    affiliations,
    honours,
    otherSources,
    liquidityEvents,
    superYachts,
    privateJets,
    otherInfo,
  };
}

/** Alias kept for forward-compatibility if a detail endpoint is ever added. */
export const profileDetailFromApi = profileFromApi;

export function paginatedFromApi(payload) {
  return {
    content: Array.isArray(payload.content) ? payload.content.map(profileFromApi) : [],
    count: Number(payload.count ?? 0),
    page: Number(payload.page ?? 1),
    total: Number(payload.total ?? 0),
  };
}

// -- ApiProfile adapter (research_profile.result) ----------------------------
//
// ApiProfile (the shape written to research_profiles.result) is a nested,
// restructured schema unrelated to the flat legacy `Profile` shape used by
// /search — it is NOT a renamed/re-keyed version of it, so it gets its own
// adapter rather than reusing profileFromApi. The output still uses the same
// UI field names as profileFromApi wherever a clean mapping exists, so
// ProfileView.jsx can render either source through the same component.

const RISK_CATEGORY_LABELS = [
  ['litigation', 'Litigation'],
  ['regulatory', 'Regulatory'],
  ['adverse_media', 'Adverse media'],
  ['financial', 'Financial'],
  ['political', 'Political (PEP)'],
  ['sanctions', 'Sanctions'],
  ['esg', 'ESG'],
  ['geographic', 'Geographic'],
];

const SEVERITY_RANK = { Nil: 0, Low: 1, Medium: 2, High: 3 };

// Splits a single formatted role/affiliation period string ("2015–Present",
// "2015–2020", "2015") into {from, to}, so the existing TimelineItem /
// `c.to === 'Present'` logic (built for separate from/to dates) keeps working.
function parseYearRange(str, isCurrent) {
  const s = clean(str);
  if (!s) return { from: '-', to: isCurrent ? 'Present' : '-' };
  if (/present/i.test(s)) {
    const from = s.split(/[–—-]/)[0].trim();
    return { from: from || '-', to: 'Present' };
  }
  const parts = s.split(/[–—-]/).map((x) => x.trim()).filter(Boolean);
  if (parts.length >= 2) return { from: parts[0], to: parts[1] };
  if (parts.length === 1) return { from: parts[0], to: isCurrent ? 'Present' : parts[0] };
  return { from: '-', to: isCurrent ? 'Present' : '-' };
}

function riskToneFromCategories(categories) {
  const maxRank = categories.reduce((m, c) => Math.max(m, SEVERITY_RANK[c.severity] ?? 0), 0);
  if (maxRank >= 3) return 'hit';
  if (maxRank >= 2) return 'review';
  return 'cleared';
}

function riskScoreFromCategories(categories) {
  const maxRank = categories.reduce((m, c) => Math.max(m, SEVERITY_RANK[c.severity] ?? 0), 0);
  return [1.6, 3.2, 4.2, 7.8][maxRank] ?? 1.6;
}

export function apiProfileResultFromApi(api, meta = {}) {
  const identity = api.identity_details || {};
  const geo = api.citizenship_and_geographic_details || {};
  const familyStatus = api.family_status || {};
  const background = api.personal_and_professional_background || {};
  const familyDetails = api.family_details || {};
  const netWorth = api.net_worth || {};
  const social = api.social_media_links || {};

  const interests = Array.isArray(api.interests_and_passions)
    ? api.interests_and_passions.map((i) => i.interest_type_detail || i.interest_type).filter(Boolean)
    : [];

  // Career — professional_roles[] replaces companies[]; each role carries a
  // single formatted period string instead of separate from/to dates.
  const career = (api.professional_roles || [])
    .map((r) => {
      const isCurrent = r.role_type === 'Current' || r.role_type === 'Primary';
      const { from, to } = parseYearRange(r.year, isCurrent);
      return {
        id: '',
        role: clean(r.position),
        company: clean(r.company_name),
        industry: clean(r.industry),
        from,
        to,
        details: clean(r.details),
        employmentType: clean(r.role_type),
      };
    })
    .sort((a, b) => {
      const aPresent = a.to === 'Present' ? 1 : 0;
      const bPresent = b.to === 'Present' ? 1 : 0;
      if (aPresent !== bPresent) return bPresent - aPresent;
      return String(b.from).localeCompare(String(a.from));
    });

  // Education
  const education = (api.education || [])
    .map((e) => ({
      type: clean(e.education_type_name),
      degree: clean(e.name_of_course),
      school: clean(e.institution_name),
      year: [clean(e.from_year), clean(e.to_year)].filter(Boolean).join('–'),
    }))
    .sort((a, b) => String(b.year).localeCompare(String(a.year)));

  // Family
  const family = (familyDetails.family_members || []).map((f) => ({
    id: '',
    name: clean(f.member_name),
    relationship: clean(f.relationship),
    membershipType: clean(f.description),
    initials: personInitials(f.member_name),
  }));

  // Network
  const network = (api.network || []).map((n) => ({
    name: clean(n.person_name),
    details: [
      [clean(n.designation), clean(n.company)].filter(Boolean).join(' at '),
      clean(n.relationship_details),
    ].filter(Boolean).join(' — '),
    initials: personInitials(n.person_name),
  }));

  const fullInterests = (api.interests_and_passions || []).map((i) => ({
    type: clean(i.interest_type),
    typeDetail: clean(i.interest_type_detail) || clean(i.interest_type),
    details: clean(i.details),
  }));

  // Awards & recognitions — structured, no string-parsing needed.
  const awards = (api.awards_and_recognitions || []).map((a) => ({
    year: clean(a.year),
    title: clean(a.recognition),
    org: clean(a.recognising_entity),
  }));

  // Net worth assets — unified list grouped by asset_type (replaces the old
  // separate listed/unlisted/other/jets/yachts/former arrays).
  const rawAssets = (netWorth.assets || []).map((a) => ({
    name: clean(a.asset_name),
    assetType: clean(a.asset_type) || 'Other',
    value: typeof a.value_usd === 'number' ? a.value_usd : null,
    valueDisplay: formatUsd(a.value_usd),
    details: clean(a.details),
  }));
  const assetGroupMap = new Map();
  for (const a of rawAssets) {
    if (!assetGroupMap.has(a.assetType)) assetGroupMap.set(a.assetType, { type: a.assetType, items: [], total: 0 });
    const group = assetGroupMap.get(a.assetType);
    group.items.push(a);
    if (Number.isFinite(a.value)) group.total += a.value;
  }
  const assetGroups = Array.from(assetGroupMap.values());

  // Affiliations (merge affiliations + philanthropy)
  const affiliations = [
    ...(api.affiliations || []).map((a) => ({
      org: clean(a.affiliation_name),
      type: clean(a.affiliation_type),
      position: clean(a.role_or_connection),
      location: '',
      year: clean(a.year),
      value: '',
      overview: clean(a.details),
      category: 'affiliation',
    })),
    ...(api.philanthropy || []).map((p) => ({
      org: clean(p.organisation_name),
      type: clean(p.organisation_type),
      position: clean(p.position),
      location: clean(p.organisation_location),
      year: clean(p.year),
      value: formatUsd(p.estimated_usd_value),
      overview: clean(p.overview),
      category: 'philanthropic',
    })),
  ];

  // Risk profile — 8 structured categories, each with severity + findings.
  const riskProfile = api.risk_profile || {};
  const riskCategories = RISK_CATEGORY_LABELS.map(([key, label]) => {
    const cat = riskProfile[key] || {};
    return {
      key,
      label,
      severity: cat.severity || 'Nil',
      findings: (cat.findings || []).map((f) => ({
        year: clean(f.year),
        subject: clean(f.subject_or_entity),
        status: clean(f.status),
        details: clean(f.details),
      })),
    };
  });
  const tone = riskToneFromCategories(riskCategories);
  const adverseMediaCount = riskCategories.find((c) => c.key === 'adverse_media')?.findings.length || 0;

  // News
  const fullNews = (api.news_and_developments || [])
    .map((n) => ({
      date: clean(n.date),
      source: clean(n.source),
      newsType: clean(n.news_type),
      headline: clean(n.headline),
      details: clean(n.details),
      link: clean(n.link),
      personalOverview: clean(n.personal_overview),
      companyOverview: clean(n.company_overview),
    }))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));

  // Advisors
  const advisorsList = (api.advisors_and_service_providers || []).map((a) => ({
    overview: clean(a.overview),
    type: clean(a.advisor_type),
    relationship: clean(a.relationship),
    company: clean(a.advisor_company),
    name: clean(a.advisor_name),
    country: clean(a.country),
    city: clean(a.city),
    website: clean(a.website),
    email: clean(a.email),
  }));

  // Contacts
  const contacts = (api.contact_details || [])
    .map((c) => ({
      id: '',
      overview: clean(c.overview),
      type: clean(c.contact_type_name),
      isPrimary: c.primary_contact === 'Primary',
      officeName: clean(c.office_name),
      address1: clean(c.address_line_1),
      address2: clean(c.address_line_2),
      country: clean(c.country),
      city: clean(c.city),
      postalCode: clean(c.postal_code),
      website: clean(c.website),
      email: clean(c.email),
      phone: clean(c.telephone),
      personalEmail: clean(c.personal_email),
      mobile: clean(c.mobile_number),
    }))
    .sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));

  const netWorthDisplay = formatUsd(netWorth.estimated_net_worth_usd) || clean(netWorth.net_worth_tier) || '-';

  return {
    // --- search card fields (unused by ProfileView, kept for shape parity) ---
    id: meta.id || '',
    name: clean(identity.full_name),
    initials: initialsFrom(identity.full_name),
    headshotUrl: clean(api.headshot_image_url),
    gender: clean(identity.gender),
    nationality: (geo.citizenship_country || []).map(clean).filter(Boolean).join(', '),
    domicile: clean(geo.domicile_country),
    residence: [clean(geo.current_location_city), clean(geo.current_location_country)].filter(Boolean).join(', '),
    industry: clean((background.primary_industry || [])[0]),
    tier: clean(netWorth.net_worth_tier),
    netWorth: netWorthDisplay,
    almaMater: '',
    interests,
    company: '',
    role: '',
    pep: false,
    sanctions: false,
    adverseMedia: adverseMediaCount,
    riskScore: riskScoreFromCategories(riskCategories),
    riskTone: tone,
    keywords: [],
    updated: meta.updated || '',

    // --- detail view fields (ProfileView) ---
    secondaryId: meta.id || '',
    title: clean(identity.title),
    knownAs: (identity.alias_names || []).map(clean).filter(Boolean).join(', '),
    city: clean(geo.current_location_city),
    country: clean(geo.current_location_country),
    age: clean(identity.age),
    dateOfBirth: clean(identity.date_of_birth),
    yearOfBirth: '',
    netWorthRange: '',
    wealthRange: clean(netWorth.net_worth_tier),
    liquidityWealth: formatUsd(netWorth.estimated_liquid_net_worth_usd) || '-',
    sourceOfWealth: '',
    bio: clean(api.summary),
    maritalStatus: clean(familyStatus.marital_status),
    spouseName: '',
    numberOfChildren: clean(familyStatus.number_of_children),
    religion: clean(background.religion),
    ethnicity: clean(background.ethnicity),
    otherNationality: '',
    twitterLink: '',
    linkedinLink: clean(social.linkedin_link),
    facebookLink: clean(social.facebook_link),
    personalWebsite: clean(social.personal_website),
    instagramLink: clean(social.instagram_link),
    youtubeLink: clean(social.youtube_link),
    tiktokLink: clean(social.tiktok_link),
    // arrays
    education,
    career,
    languages: (background.languages_spoken || []).map(clean).filter(Boolean),
    family,
    familyBackground: clean(familyDetails.family_background),
    network,
    fullInterests,
    listedOwnership: [],
    unlistedOwnership: [],
    otherAssets: [],
    formerAssets: [],
    fullNews,
    litigationList: [],
    bankruptcyList: [],
    contacts,
    advisorsList,
    affiliations,
    honours: [],
    awards,
    otherSources: [],
    liquidityEvents: [],
    superYachts: [],
    privateJets: [],
    otherInfo: [],
    assetGroups,
    riskCategories,
  };
}

// -- research-profiles adapters ----------------------------------------------

/**
 * The `citations` map on a ResearchProfile is polymorphic per the OpenAPI spec:
 * each value can be a single ResearchCitation, an array of them, or an array of
 * { [key]: ResearchCitation } objects. Flatten all three into one flat list
 * rather than assuming a single shape.
 */
function normalizeCitations(raw) {
  const out = [];
  if (!raw || typeof raw !== 'object') return out;
  for (const [field, value] of Object.entries(raw)) {
    const items = Array.isArray(value) ? value : [value];
    for (const item of items) {
      if (!item || typeof item !== 'object') continue;
      if (item.source_url) {
        out.push({ field, sourceUrl: item.source_url, snippet: clean(item.snippet) });
      } else {
        for (const [subField, citation] of Object.entries(item)) {
          if (citation && citation.source_url) {
            out.push({ field: `${field}.${subField}`, sourceUrl: citation.source_url, snippet: clean(citation.snippet) });
          }
        }
      }
    }
  }
  return out;
}

export function researchProfileFromApi(api) {
  return {
    id: api.id || '',
    requestedBy: api.requested_by || '',
    status: api.status || 'pending',
    name: clean(api.name),
    context: clean(api.context),
    createdAt: clean(api.created_at),
    updatedAt: clean(api.updated_at),
    completedAt: clean(api.completed_at),
    errorMessage: clean(api.error_message),
    result: api.result
      ? apiProfileResultFromApi(api.result, {
          id: api.id,
          updated: formatUpdated(api.completed_at || api.updated_at || api.created_at),
        })
      : null,
    // Untouched nested result, kept alongside the flattened `result` above so
    // ResearchProfileView (the dedicated research_profile dossier page) can
    // adapt it separately via dossierProfileFromResult without a second
    // network round-trip.
    rawResult: api.result || null,
    citations: normalizeCitations(api.citations),
  };
}

/**
 * Maps a ResearchProfileShare (POST .../share and GET /research-profiles/
 * shared/{token} share the same response shape) to the minimal shape
 * ResearchProfileView actually reads. Deliberately not routed through
 * researchProfileFromApi: a share has no status/requested_by/updated_at, and
 * piggybacking on that adapter would compute an unused apiProfileResultFromApi
 * transform and leave a misleading status: 'pending' on a completed profile.
 */
export function researchProfileShareFromApi(api) {
  return {
    id: api.id || '',
    result: { updated: formatUpdated(api.completed_at || api.created_at) },
    rawResult: api.result || null,
    citations: normalizeCitations(api.citations),
    expiresAt: clean(api.expires_at),
  };
}

/**
 * Maps ResearchProfileRegenerationStatus (GET .../regeneration) to the shape
 * consumed by useProfileRegeneration: every regeneration currently active for
 * the profile, each `{ kind: 'full' | 'segment', fieldName, startedAt }`.
 * `fieldName` is only meaningful for a 'segment' job (the ApiProfile field
 * being regenerated).
 *
 * `active` is a list because segment runs for different fields can proceed
 * side by side; a full run, when present, is always the only entry. It
 * replaced the flat kind/field_name/started_at this response used to carry,
 * which couldn't represent more than one job at once.
 */
export function regenerationStatusFromApi(api) {
  const active = Array.isArray(api.active) ? api.active : [];
  return {
    inProgress: !!api.in_progress,
    active: active.map((job) => ({
      kind: job.kind || 'segment',
      fieldName: job.field_name || null,
      startedAt: clean(job.started_at),
    })),
  };
}

/**
 * Maps a ResearchProfileSummary — the listing shape from GET
 * /research-profiles, which deliberately omits result/citations/raw_result
 * (plus updated_at and error_message) so the heavy JSON columns are never
 * fetched for a list request. Full detail is GET /research-profiles/{id}
 * (researchProfileFromApi) only, which is why the list must not be used as
 * a source for the dossier page.
 */
export function researchProfileSummaryFromApi(api) {
  return {
    id: api.id || '',
    requestedBy: api.requested_by || '',
    status: api.status || 'pending',
    name: clean(api.name),
    context: clean(api.context),
    createdAt: clean(api.created_at),
    completedAt: clean(api.completed_at),
  };
}

export function paginatedResearchFromApi(payload) {
  return {
    content: Array.isArray(payload.content) ? payload.content.map(researchProfileSummaryFromApi) : [],
    count: Number(payload.count ?? 0),
    page: Number(payload.page ?? 1),
    total: Number(payload.total ?? 0),
  };
}

// -- dossier adapter (ResearchProfileView / "Profile Dossier v2" design) ----
//
// research_profile results get their own page (ResearchProfileView), separate
// from the flat-Profile-shaped ProfileView used for HNWI (/search) results —
// see the design's field names (p.identity, p.geo, p.familyStatus, …), which
// mirror profile_schema_v2's nested section names one-for-one. Rather than
// route through apiProfileResultFromApi's flattened UI shape (built for the
// old shared ProfileView), this adapts the *raw* nested result directly so
// the dossier page can reproduce the design faithfully.

function usdDossier(v) {
  const n = typeof v === 'number' ? v : Number(v);
  if (v == null || v === '' || !Number.isFinite(n)) return '-';
  const neg = n < 0;
  const abs = Math.abs(n);
  let s;
  if (abs >= 1e9) s = `${(abs / 1e9).toFixed(2)}B`;
  else if (abs >= 1e6) s = `${(abs / 1e6).toFixed(0)}M`;
  else if (abs >= 1e3) s = `${(abs / 1e3).toFixed(0)}K`;
  else s = String(abs);
  return (neg ? '−USD ' : 'USD ') + s;
}

const DOSSIER_RISK_LABELS = [
  ['litigation', 'Litigation'],
  ['regulatory', 'Regulatory'],
  ['adverse_media', 'Adverse media'],
  ['financial', 'Financial'],
  ['political', 'Political'],
  ['sanctions', 'Sanctions'],
  ['esg', 'ESG'],
  ['geographic', 'Geographic'],
];
const DOSSIER_SEV_ORDER = { Nil: 0, Low: 1, Medium: 2, High: 3 };
const DOSSIER_TYPE_COLORS = ['#BFB4E4', '#AFCDEF', '#A9D8C4', '#F2D6A3', '#D9C3DE', '#CFD5E2'];
const DOSSIER_SOCIAL_META = [
  ['personal_website', 'Personal website', 'globe'],
  ['linkedin_link', 'LinkedIn', 'linkedin'],
  ['facebook_link', 'Facebook', 'facebook'],
  ['instagram_link', 'Instagram', 'instagram'],
  ['youtube_link', 'YouTube', 'youtube'],
  ['tiktok_link', 'TikTok', 'tiktok'],
];

function dossierInitials(name) {
  const parts = String(name || '')
    .replace(/[^A-Za-z .]/g, '')
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 1 && w !== 'Dr.' && w !== 'Sir');
  if (!parts.length) return '—';
  return (parts[0][0] + (parts[parts.length - 1][0] || '')).toUpperCase();
}

function dossierUrl(raw) {
  if (!raw) return '';
  return /^https?:/.test(raw) ? raw : `https://${raw}`;
}

// Sortable "how recent is this" value for a free-text date/year string —
// dossier date fields aren't guaranteed to be ISO (a plain string compare
// used to be relied on for the news sort, which breaks on formats like
// "12 Jan 2024" or "2015-Present"). Parses a real date first; falls back to
// the largest 4-digit year found in the string; "present"/"current" always
// sorts as most-recent. Unparseable/empty values sort last.
function recencySortValue(raw) {
  const s = clean(raw);
  if (!s) return -Infinity;
  if (/present|current/i.test(s)) return Infinity;
  const t = Date.parse(s);
  if (!Number.isNaN(t)) return t;
  const years = s.match(/\d{4}/g);
  if (years) return Math.max(...years.map(Number));
  return -Infinity;
}

function byRecencyDesc(dateKey) {
  return (a, b) => recencySortValue(b[dateKey]) - recencySortValue(a[dateKey]);
}

/**
 * Same "Primary first, then latest-to-oldest" ordering as the read view's
 * roles/rolesCurrent/rolesFormer (see dossierProfileFromResult below), but
 * over the raw professional_roles rows (role_type/year, not roleType/year) —
 * used by SectionEditModal's initial value so the edit list matches the
 * timeline the user is looking at, rather than raw array order.
 */
export function sortRawProfessionalRoles(list) {
  return [...(Array.isArray(list) ? list : [])].sort((x, y) => {
    const xPrimary = x?.role_type === 'Primary' ? 1 : 0;
    const yPrimary = y?.role_type === 'Primary' ? 1 : 0;
    if (xPrimary !== yPrimary) return yPrimary - xPrimary;
    return recencySortValue(y?.year) - recencySortValue(x?.year);
  });
}

/**
 * Maps a raw research_profile.result (nested ApiProfile / profile_schema_v2
 * shape) to the `p` object consumed by ResearchProfileView.
 *
 * @param {object} api - api.result as returned by GET /research-profiles (unadapted)
 * @param {object} meta - { id, updated } passed through from the parent research_profile row
 */
export function dossierProfileFromResult(api, meta = {}) {
  const a = api || {};
  const identity = a.identity_details || {};
  const geo = a.citizenship_and_geographic_details || {};
  const familyStatus = a.family_status || {};
  const background = a.personal_and_professional_background || {};
  const familyDetails = a.family_details || {};
  const netWorth = a.net_worth || {};
  const social = a.social_media_links || {};
  const riskProfile = a.risk_profile || {};

  const fullName = clean(identity.full_name);
  const title = clean(identity.title);

  // Education per the OpenAPI schema: institution_name + institution_country
  // + a from_year/to_year range (no organisation_name, no year_of_completion).
  const education = (a.education || []).map((e) => ({
    type: clean(e.education_type_name),
    institution: clean(e.institution_name),
    country: clean(e.institution_country),
    course: clean(e.name_of_course),
    year: [clean(e.from_year), clean(e.to_year)].filter(Boolean).join('–'),
  }));

  // FamilyMember per the OpenAPI schema: member_name/age/relationship/
  // description (no membership_type).
  const familyMembers = (familyDetails.family_members || []).map((m) => ({
    name: clean(m.member_name),
    age: clean(m.age),
    relationship: clean(m.relationship),
    membershipType: clean(m.description),
    initials: dossierInitials(m.member_name),
  }));

  const interests = (a.interests_and_passions || []).map((i) => ({
    type: clean(i.interest_type),
    typeDetail: clean(i.interest_type_detail) || clean(i.interest_type),
    details: clean(i.details),
  }));

  const awards = (a.awards_and_recognitions || []).map((w) => ({
    year: clean(w.year),
    recognition: clean(w.recognition),
    org: clean(w.recognising_entity),
  }));

  // Primary role(s) first, everything else latest-to-oldest by year — applied
  // before the current/former split below so both groups come out ordered
  // (Former has no Primary entries, so it's a plain recency sort; Current
  // keeps Primary pinned to the top followed by the rest by recency).
  const roles = (a.professional_roles || [])
    .map((r) => ({
      company: clean(r.company_name),
      position: clean(r.position),
      roleType: clean(r.role_type) || 'Current',
      year: clean(r.year),
      country: clean(r.country),
      industry: clean(r.industry),
      details: clean(r.details),
    }))
    .sort((x, y) => {
      const xPrimary = x.roleType === 'Primary' ? 1 : 0;
      const yPrimary = y.roleType === 'Primary' ? 1 : 0;
      if (xPrimary !== yPrimary) return yPrimary - xPrimary;
      return recencySortValue(y.year) - recencySortValue(x.year);
    });
  const rolesCurrent = roles.filter((r) => r.roleType !== 'Former');
  const rolesFormer = roles.filter((r) => r.roleType === 'Former');
  const primaryRole = roles.find((r) => r.roleType === 'Primary') || rolesCurrent[0] || roles[0] || {};

  // Net worth — composition bar grouped by positive-value asset type, colours
  // assigned by descending share (mirrors the design's byType/typeColor logic).
  const rawAssets = (netWorth.assets || []).map((asset) => {
    const value = typeof asset.value_usd === 'number' ? asset.value_usd : Number(asset.value_usd);
    return {
      name: clean(asset.asset_name),
      type: clean(asset.asset_type) || 'Other',
      value: Number.isFinite(value) ? value : null,
      details: clean(asset.details),
    };
  });
  const positiveByType = new Map();
  rawAssets.forEach((asset) => {
    if (!Number.isFinite(asset.value) || asset.value <= 0) return;
    positiveByType.set(asset.type, (positiveByType.get(asset.type) || 0) + asset.value);
  });
  const sortedTypes = Array.from(positiveByType.entries()).sort((x, y) => y[1] - x[1]);
  const grossTotal = sortedTypes.reduce((s, [, v]) => s + v, 0);
  const typeColor = new Map(sortedTypes.map(([type], i) => [type, DOSSIER_TYPE_COLORS[i % DOSSIER_TYPE_COLORS.length]]));
  const typeSegments = sortedTypes.map(([type, value]) => {
    const pct = grossTotal > 0 ? (value / grossTotal) * 100 : 0;
    return {
      type,
      valueFmt: usdDossier(value),
      pct,
      pctLabel: `${pct.toFixed(1)}%`,
      color: typeColor.get(type) || 'var(--neutral-300)',
    };
  });
  const assets = rawAssets.map((asset) => ({
    ...asset,
    valueFmt: usdDossier(asset.value),
    negative: Number.isFinite(asset.value) && asset.value < 0,
    color: Number.isFinite(asset.value) && asset.value < 0 ? '#EDB6B0' : typeColor.get(asset.type) || 'var(--neutral-200)',
  }));

  const affiliations = (a.affiliations || []).map((af) => ({
    name: clean(af.affiliation_name),
    type: clean(af.affiliation_type),
    role: clean(af.role_or_connection),
    year: clean(af.year),
    details: clean(af.details),
  }));

  // PhilanthropicActivity per the OpenAPI schema: overview + a single
  // organisation_type field + focus_area (estimated_usd_value is a number).
  const philanthropy = (a.philanthropy || []).map((ph) => ({
    org: clean(ph.organisation_name),
    type: clean(ph.organisation_type),
    primary: clean(ph.primary_philanthropy) || 'Other',
    overview: clean(ph.overview),
    focusArea: clean(ph.focus_area),
    position: clean(ph.position),
    location: clean(ph.organisation_location),
    year: clean(ph.year),
    valueFmt: usdDossier(ph.estimated_usd_value),
  }));

  // RiskCategory per the OpenAPI schema carries only severity + findings —
  // no summary field.
  const riskCategories = DOSSIER_RISK_LABELS.map(([key, label]) => {
    const cat = riskProfile[key] || {};
    const severity = cat.severity || 'Nil';
    const findings = (cat.findings || [])
      .map((f) => ({
        year: clean(f.year),
        subject: clean(f.subject_or_entity),
        status: clean(f.status),
        details: clean(f.details),
      }))
      .sort(byRecencyDesc('year'));
    return {
      key,
      label,
      severity,
      countLabel: findings.length === 0 ? 'No findings' : findings.length === 1 ? '1 finding' : `${findings.length} findings`,
      findings,
    };
  });
  const topSeverity = riskCategories.reduce(
    (top, c) => (DOSSIER_SEV_ORDER[c.severity] > DOSSIER_SEV_ORDER[top] ? c.severity : top),
    'Nil',
  );

  const news = (a.news_and_developments || [])
    .map((n) => ({
      date: clean(n.date),
      source: clean(n.source),
      type: clean(n.news_type),
      headline: clean(n.headline),
      details: clean(n.details),
      link: clean(n.link),
      href: dossierUrl(clean(n.link)),
    }))
    .sort(byRecencyDesc('date'));

  const network = (a.network || []).map((n) => ({
    name: clean(n.person_name),
    designation: clean(n.designation),
    company: clean(n.company),
    details: clean(n.relationship_details),
    initials: dossierInitials(n.person_name),
  }));

  const advisors = (a.advisors_and_service_providers || []).map((adv) => ({
    type: clean(adv.advisor_type),
    relationship: clean(adv.relationship),
    company: clean(adv.advisor_company),
    name: clean(adv.advisor_name),
    location: [clean(adv.city), clean(adv.country)].filter(Boolean).join(', '),
    website: clean(adv.website),
    email: clean(adv.email),
  }));

  // ContactDetail per the OpenAPI schema: address_line_1/address_line_2/
  // state_or_region (no address1/address2/county).
  const contacts = (a.contact_details || []).map((c) => ({
    type: clean(c.contact_type_name),
    primary: clean(c.primary_contact) || 'Other',
    officeName: clean(c.office_name),
    address: [c.address_line_1, c.address_line_2, c.city, c.state_or_region, c.postal_code, c.country]
      .map(clean)
      .filter(Boolean)
      .join(', '),
    telephone: clean(c.telephone),
    email: clean(c.email),
    website: clean(c.website),
    personalEmail: clean(c.personal_email),
    mobile: clean(c.mobile_number),
  }));

  const socialLinks = DOSSIER_SOCIAL_META
    .map(([key, label, icon]) => {
      const raw = clean(social[key]);
      return raw ? { label, icon, url: dossierUrl(raw) } : null;
    })
    .filter(Boolean);

  const netWorthTier = clean(netWorth.net_worth_tier);
  const primaryIndustry = cleanList(background.primary_industry);
  const citizenship = cleanList(geo.citizenship_country);
  const domicile = clean(geo.domicile_country);
  const birthplace = [clean(geo.birthplace_city), clean(geo.birthplace_state_or_region), clean(geo.birthplace_country)]
    .filter(Boolean)
    .join(', ');
  const currentLocation = [
    clean(geo.current_location_city),
    clean(geo.current_location_state_or_region),
    clean(geo.current_location_country),
  ].filter(Boolean).join(', ');
  const residence = [clean(geo.current_location_city), clean(geo.current_location_country)].filter(Boolean).join(', ');

  return {
    id: meta.id || '',
    name: fullName || '-',
    nameWithTitle: [title, fullName].filter(Boolean).join(' ') || '-',
    aliases: cleanList(identity.alias_names, ' · '),
    initials: dossierInitials(fullName),
    headshotUrl: clean(a.headshot_image_url),
    eyebrow: netWorthTier,
    primaryRole: primaryRole.position || '-',
    primaryCompany: primaryRole.company || '',
    recordId: meta.id || '-',
    profileDate: meta.updated || '-',
    ageGender: [clean(identity.age), clean(identity.gender)].filter(Boolean).join(' · ') || '-',

    identity: {
      title,
      fullName,
      aliasNames: cleanList(identity.alias_names),
      dateOfBirth: clean(identity.date_of_birth),
      age: clean(identity.age),
      gender: clean(identity.gender),
    },
    geo: { citizenship, domicile, birthplace, currentLocation, residence },
    familyStatus: {
      maritalStatus: clean(familyStatus.marital_status),
      numberOfChildren: clean(familyStatus.number_of_children),
    },
    background: {
      languages: cleanList(background.languages_spoken),
      primaryIndustry,
      ethnicity: clean(background.ethnicity),
      religion: clean(background.religion),
    },

    bio: clean(a.summary),

    education,
    family: { background: clean(familyDetails.family_background), members: familyMembers },
    interests,
    interestTags: interests.map((i) => i.typeDetail).filter(Boolean),
    awards,
    rolesCurrent,
    rolesFormer,
    rolesCurrentLabel: `Current roles · ${rolesCurrent.length}`,
    rolesFormerLabel: `Former roles · ${rolesFormer.length}`,

    netWorth: {
      tier: netWorthTier || '-',
      totalFmt: usdDossier(netWorth.estimated_net_worth_usd),
      liquidFmt: usdDossier(netWorth.estimated_liquid_net_worth_usd),
      typeSegments,
      assets,
    },

    affiliations,
    philanthropy,
    risk: { categories: riskCategories, topSeverity },
    news,
    network,
    advisors,
    contacts,
    socialLinks,
  };
}

// Section ids used by ResearchProfileView's nav/sources — a citation's
// `field` (e.g. "identity_details.full_name") is bucketed by its top-level
// segment matching one of these schema section names.
const DOSSIER_SECTION_FIELD_PREFIXES = {
  key: ['identity_details', 'citizenship_and_geographic_details', 'family_status', 'personal_and_professional_background'],
  summary: ['summary'],
  education: ['education'],
  family: ['family_details'],
  interests: ['interests_and_passions'],
  roles: ['professional_roles'],
  networth: ['net_worth'],
  risk: ['risk_profile'],
  awards: ['awards_and_recognitions'],
  affiliations: ['affiliations'],
  philanthropy: ['philanthropy'],
  contact: ['contact_details', 'social_media_links'],
  network: ['network'],
  advisors: ['advisors_and_service_providers'],
  news: ['news_and_developments'],
};

function hostFromUrl(url) {
  try {
    return new URL(dossierUrl(url)).hostname.replace(/^www\./, '');
  } catch {
    return url || '';
  }
}

/**
 * Buckets a research_profile's normalized citations by dossier section id,
 * for the per-section "N sources" chip.
 */
export function groupDossierCitations(citations) {
  const groups = {};
  const seen = {};
  for (const key of Object.keys(DOSSIER_SECTION_FIELD_PREFIXES)) {
    groups[key] = [];
    seen[key] = new Set();
  }
  for (const c of citations || []) {
    if (!c?.sourceUrl) continue;
    const top = String(c.field || '').split('.')[0];
    const sectionKey =
      Object.keys(DOSSIER_SECTION_FIELD_PREFIXES).find((k) => DOSSIER_SECTION_FIELD_PREFIXES[k].includes(top)) || 'key';
    const url = dossierUrl(c.sourceUrl);
    // Several fields within the same section (e.g. two identity_details
    // sub-fields) can cite the same source_url — collapse those to one
    // entry so the "N sources" chip doesn't repeat the same link.
    if (seen[sectionKey].has(url)) continue;
    seen[sectionKey].add(url);
    groups[sectionKey].push({
      title: c.snippet || (top ? top.replace(/_/g, ' ') : 'Source'),
      meta: hostFromUrl(c.sourceUrl),
      url,
    });
  }
  return groups;
}

// -- wealth-signals adapters (/events, /people) -----------------------------
//
// The signals endpoints return rows already formatted for display, keyed by
// the list (eventRows/eventCount, peopleRows/peopleCount). These normalise
// both into the { content, total, page, totalPages } shape the list pages use.

function signalsPageFromApi(payload, rowsKey, countKey) {
  return {
    content: Array.isArray(payload?.[rowsKey]) ? payload[rowsKey] : [],
    total: Number(payload?.[countKey] ?? 0),
    page: Number(payload?.page ?? 1),
    totalPages: Math.max(1, Number(payload?.totalPages ?? 1)),
  };
}

export function signalEventsPageFromApi(payload) {
  return signalsPageFromApi(payload, 'eventRows', 'eventCount');
}

export function signalPeoplePageFromApi(payload) {
  return signalsPageFromApi(payload, 'peopleRows', 'peopleCount');
}
